import React, { useEffect, useState } from 'react';
import { MapPin, LogIn, LogOut, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface GeofenceEvent {
  id: string;
  device_id: string;
  device_nickname: string;
  geofence_name: string;
  event_type: 'entry' | 'exit';
  occurred_at: string;
  latitude: number;
  longitude: number;
}

interface GeofenceNotificationsProps {
  userId?: string;
  limit?: number;
}

const GeofenceNotifications: React.FC<GeofenceNotificationsProps> = ({ 
  userId, 
  limit = 5 
}) => {
  const [events, setEvents] = useState<GeofenceEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;

    const fetchEvents = async () => {
      try {
        const { data, error } = await supabase
          .rpc('get_recent_geofence_events', { 
            p_user_id: userId,
            p_limit: limit 
          });

        if (error) {
          console.error('Error fetching geofence events:', error);
        } else {
          setEvents(data || []);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();

    // Set up real-time subscription for new events
    const subscription = supabase
      .channel('geofence-events')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'geofence_events',
          filter: `user_id=eq.${userId}`
        },
        async (payload) => {
          // Fetch the complete event details
          const { data } = await supabase
            .rpc('get_recent_geofence_events', { 
              p_user_id: userId,
              p_limit: 1 
            });
          
          if (data && data.length > 0) {
            setEvents(prev => [data[0], ...prev.slice(0, limit - 1)]);
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userId, limit]);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No recent geofence events</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
        <MapPin className="w-4 h-4 mr-2" />
        Location Events
      </h3>
      
      {events.map((event) => (
        <div
          key={event.id}
          className={`p-3 rounded-lg border-l-4 ${
            event.event_type === 'entry' 
              ? 'bg-green-50 border-green-500' 
              : 'bg-orange-50 border-orange-500'
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-2">
              {event.event_type === 'entry' ? (
                <LogIn className="w-4 h-4 text-green-600 mt-0.5" />
              ) : (
                <LogOut className="w-4 h-4 text-orange-600 mt-0.5" />
              )}
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {event.device_nickname || event.device_id}
                </p>
                <p className="text-xs text-gray-600">
                  {event.event_type === 'entry' ? 'Entered' : 'Exited'} {event.geofence_name}
                </p>
              </div>
            </div>
            <div className="flex items-center text-xs text-gray-500">
              <Clock className="w-3 h-3 mr-1" />
              {formatTime(event.occurred_at)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default GeofenceNotifications;